import React, { useState, useEffect } from "react";
import { Button, Input, notification, Modal, Upload } from "antd";
import {
  SendOutlined,
  DownloadOutlined,
  CheckCircleOutlined,
  InboxOutlined
} from "@ant-design/icons";
import { v4 as uuidv4 } from "uuid";
import { useHistory } from "react-router-dom";
import { useDispatch } from "react-redux";
import request from "helper/request";
import ImportLocations from "modules/client/routes/components/exports";
import { setGlobalLoading } from "app/global/slice";
import { convertFileToText } from "../utils/fileTransformer";
import "./AIImportPage.scss";

const { TextArea } = Input;
const { Dragger } = Upload;

const AIImportPage = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [conversationId] = useState(uuidv4());
  const [inputText, setInputText] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [csvPreview, setCsvPreview] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [showImportModal, setShowImportModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null); // { rowIndex, columnName }
  const [editValue, setEditValue] = useState("");
  const [csvFading, setCsvFading] = useState(false);
  const [visibleRows, setVisibleRows] = useState(20); // Start with 20 rows visible
  const [showImportView, setShowImportView] = useState(false); // Track if showing import component
  const [totalStop, setTotalStop] = useState(0);
  const [currentStop, setCurrentStop] = useState(0);

  // Determine if we're in admin context based on the current path
  const isAdmin = history.location.pathname.includes("/admin");

  // Clear global loading state on mount to prevent grey screen on navigation
  useEffect(() => {
    dispatch(setGlobalLoading({ isLoading: false }));
  }, [dispatch]);

  const scrollToBottom = () => {
    setTimeout(() => {
      const container = document.querySelector(".ai-chat-messages");
      if (container) {
        container.scrollTop = container.scrollHeight;
      }
    }, 100);
  };

  const handleSubmit = async () => {
    if (!inputText && !selectedFile && !csvData) {
      notification.warning({
        message: "Please enter text or attach a file",
        placement: "topRight"
      });
      return;
    }

    const userMessage = inputText || `[File: ${selectedFile?.name}]`;

    // Add user message to chat
    setChatMessages((prev) => [...prev, { role: "user", content: userMessage }]);

    // Clear input immediately
    const currentInputText = inputText;
    const currentFile = selectedFile;
    const currentCsvData = csvData; // Keep track of current CSV data for follow-up requests
    setInputText("");
    setSelectedFile(null);

    setIsLoading(true);
    scrollToBottom();

    try {
      let textToSend = currentInputText;

      // Convert file to text if present
      if (currentFile) {
        try {
          console.log("[AI Import] Converting file:", currentFile.name, currentFile.type);
          const fileText = await convertFileToText(currentFile);
          console.log(
            "[AI Import] File converted successfully. Text length:",
            fileText?.length
          );
          console.log(
            "[AI Import] First 200 chars of converted text:",
            fileText?.substring(0, 200)
          );

          // Combine user input with file content
          textToSend = currentInputText
            ? `${currentInputText}\n\n--- File Content (${currentFile.name}) ---\n${fileText}`
            : fileText;

          console.log("[AI Import] Final textToSend length:", textToSend?.length);
        } catch (error) {
          console.error("File conversion error:", error);
          setIsLoading(false);
          notification.error({
            message: "File Conversion Failed",
            description: error.message || "Failed to convert file to text",
            placement: "topRight"
          });
          return;
        }
      }

      const formData = new FormData();
      formData.append("conversationId", conversationId);

      // Determine what text to send based on context
      let finalTextToSend = textToSend;

      // If we have CSV data from a previous response and no new file, always include it
      if (currentCsvData && !currentFile) {
        if (textToSend) {
          // Combine new message with previous CSV data
          finalTextToSend = `${textToSend}\n\n--- Previous CSV Data ---\n${currentCsvData}`;
          console.log(
            "[AI Import] Combining user input with previous CSV data. Combined length:",
            finalTextToSend.length
          );
        } else {
          // No new message, just send the previous CSV data
          finalTextToSend = currentCsvData;
          console.log(
            "[AI Import] No new input, sending previous CSV data. Length:",
            finalTextToSend.length
          );
        }
      }

      // Send text content ONCE (either from user input, converted file, or combined with previous CSV)
      if (finalTextToSend) {
        console.log(
          "[AI Import] Appending finalTextToSend to formData. Length:",
          finalTextToSend.length
        );
        formData.append("text", finalTextToSend);
      } else {
        console.warn("[AI Import] WARNING: No text to append!");
      }

      formData.append("language", "en");
      console.log("[AI Import] FormData prepared. Sending to backend...");

      const data = await request.post("/ai-import/process", formData, {
        headers: {
          "Content-Type": "multipart/form-data"
        },
        timeout: 180000 // 3 minute timeout for AI processing
      });

      setIsLoading(false);

      console.log("AI Import Response:", {
        hasMessage: !!data.message,
        hasCsvData: !!data.returnedFileData,
        csvLength: data.returnedFileData?.length,
        messagePreview: data.message?.substring(0, 100)
      });

      // Add AI response to chat
      setChatMessages((prev) => [...prev, { role: "assistant", content: data.message }]);

      // Handle CSV data
      if (data.returnedFileData) {
        console.log("Updating CSV data and preview");

        // Trigger fade-out animation if CSV preview already exists
        if (csvPreview) {
          setCsvFading(true);

          // Wait for fade-out, then update data and fade back in
          setTimeout(() => {
            updateCsvData(data.returnedFileData);
            setCsvFading(false);
          }, 300); // 300ms fade-out
        } else {
          // First time showing CSV, no animation needed
          updateCsvData(data.returnedFileData);
        }
      }

      scrollToBottom();
    } catch (error) {
      console.error("AI Import error:", error);
      setIsLoading(false);

      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to process your request. Please try again.";

      // Add error message to chat
      setChatMessages((prev) => [
        ...prev,
        { role: "error", content: `Sorry, I encountered an error: ${errorMessage}` }
      ]);

      notification.error({
        message: "Processing Failed",
        description: errorMessage,
        placement: "topRight"
      });

      scrollToBottom();
    }
  };

  // Helper function to escape CSV values
  // ALWAYS quote all fields to ensure maximum compatibility and avoid parsing issues
  const escapeCsvValue = (value) => {
    if (value == null) return '""'; // Quote empty values too
    const stringValue = String(value);

    // Escape any quotes by doubling them, then wrap in quotes
    return `"${stringValue.replace(/"/g, '""')}"`;
  };

  // Helper function to regenerate CSV from preview data
  const regenerateCsvFromPreview = (headers, data) => {
    console.log("[AI Import] Regenerating CSV from preview data...");
    console.log("[AI Import] Headers:", headers);
    console.log("[AI Import] Number of data rows:", data.length);

    const headerLine = headers.map(escapeCsvValue).join(",");
    console.log("[AI Import] Escaped header line:", headerLine);

    const dataLines = data.map((row, index) => {
      const line = headers.map((header) => escapeCsvValue(row[header] || "")).join(",");
      if (index < 3) {
        console.log(`[AI Import] Row ${index + 1} escaped:`, line);
        console.log(
          `[AI Import] Row ${index + 1} Stop Address value:`,
          row["Stop Address"]
        );
      }
      return line;
    });

    const csvOutput = [headerLine, ...dataLines].join("\n");
    console.log("[AI Import] Total CSV length:", csvOutput.length);
    console.log("[AI Import] First 500 chars of CSV:", csvOutput.substring(0, 500));

    return csvOutput;
  };

  const updateCsvData = (returnedFileData) => {
    setCsvData(returnedFileData);
    setVisibleRows(20); // Reset to 20 rows when new data arrives

    // Parse CSV for preview (handles quoted fields with commas)
    // Backend now ensures proper column alignment, so we just parse without fixing
    const parseCSVLine = (line) => {
      const result = [];
      let current = "";
      let inQuotes = false;

      for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (char === '"') {
          if (inQuotes && nextChar === '"') {
            // Escaped quote
            current += '"';
            i++; // Skip next quote
          } else {
            // Toggle quote state
            inQuotes = !inQuotes;
          }
        } else if (char === "," && !inQuotes) {
          // End of field
          result.push(current);
          current = "";
        } else {
          current += char;
        }
      }
      result.push(current);

      return result;
    };

    const rows = returnedFileData.split("\n").filter((row) => row.trim());
    if (rows.length > 0) {
      const headers = parseCSVLine(rows[0]); // Parse header row
      const dataRows = rows.slice(1).map((row) => {
        const values = parseCSVLine(row); // Parse data row (backend already validated)
        const rowData = {};
        headers.forEach((header, i) => {
          rowData[header] = values[i] || "";
        });
        return rowData;
      });

      setCsvPreview({ headers, data: dataRows });

      // Regenerate CSV with proper escaping to ensure consistency
      // This normalizes the CSV format regardless of how it was received from backend
      const normalizedCsv = regenerateCsvFromPreview(headers, dataRows);
      setCsvData(normalizedCsv);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileSelect = (file) => {
    console.log("File selected:", file); // Debug log

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      notification.error({
        message: "File too large",
        description: "File size must be less than 5MB",
        placement: "topRight"
      });
      return false;
    }

    // Validate file type
    const allowedTypes = [
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.ms-excel", // For .xls files
      "text/csv",
      "text/plain",
      "application/vnd.oasis.opendocument.spreadsheet",
      "application/vnd.oasis.opendocument.text"
    ];

    // Also check file extension as a fallback (some systems don't set MIME types correctly)
    const fileName = file.name.toLowerCase();
    const hasValidExtension =
      fileName.endsWith(".pdf") ||
      fileName.endsWith(".docx") ||
      fileName.endsWith(".xlsx") ||
      fileName.endsWith(".xls") ||
      fileName.endsWith(".csv") ||
      fileName.endsWith(".txt") ||
      fileName.endsWith(".ods") ||
      fileName.endsWith(".odt");

    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      notification.error({
        message: "Invalid file type",
        description:
          "Please upload PDF, DOCX, XLSX, XLS, CSV, TXT, ODS, or ODT files only",
        placement: "topRight"
      });
      return false;
    }

    setSelectedFile(file);
    console.log("File state set to:", file.name); // Debug log
    return false; // Prevent auto-upload
  };

  const handleDownload = () => {
    if (!csvData || !csvPreview) {
      notification.warning({
        message: "No file to download",
        description: "Process a file first to generate CSV data",
        placement: "topRight"
      });
      return;
    }

    // Get first stop name for filename
    const firstStopName =
      csvPreview.data && csvPreview.data.length > 0
        ? csvPreview.data[0]["Stop Name"] || "route"
        : "route";

    // Clean filename - remove special characters
    const cleanName = firstStopName
      .replace(/[^a-zA-Z0-9]/g, "-")
      .replace(/-+/g, "-")
      .toLowerCase();

    const fileName = `converted-${cleanName}.csv`;

    const blob = new Blob([csvData], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    return fileName; // Return filename for modal
  };

  const handleAcceptAndContinue = () => {
    if (!csvData) {
      notification.warning({
        message: "No data to import",
        description: "Process a file first to generate import data",
        placement: "topRight"
      });
      return;
    }

    setShowImportModal(true);
  };

  const handleCloseImportModal = () => {
    setShowImportModal(false);
  };

  const handleImportFile = () => {
    setShowImportModal(false);
    setShowImportView(true);
  };

  const handleBackToChat = () => {
    setShowImportView(false);
  };

  const handleCellDoubleClick = (rowIndex, columnName, currentValue) => {
    setEditingCell({ rowIndex, columnName });
    setEditValue(currentValue);
  };

  const handleCellEditSave = () => {
    if (!editingCell || !csvPreview) return;

    const { rowIndex, columnName } = editingCell;

    // Update the csvPreview data
    const updatedData = [...csvPreview.data];
    updatedData[rowIndex][columnName] = editValue;
    setCsvPreview({ ...csvPreview, data: updatedData });

    // Regenerate CSV string from updated data with proper escaping
    const newCsvData = regenerateCsvFromPreview(csvPreview.headers, updatedData);
    setCsvData(newCsvData);

    // Clear editing state
    setEditingCell(null);
    setEditValue("");

    notification.success({
      message: "Cell updated",
      description: "CSV data has been updated with your changes.",
      placement: "topRight",
      duration: 2
    });
  };

  const handleCellEditCancel = () => {
    setEditingCell(null);
    setEditValue("");
  };

  const handleEditKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleCellEditSave();
    } else if (e.key === "Escape") {
      e.preventDefault();
      handleCellEditCancel();
    }
  };

  // If showing import view, render ImportLocations component
  if (showImportView) {
    return (
      <div className="ai-import-page" style={{ height: "100vh", overflow: "auto" }}>
        <div style={{ padding: "20px", maxWidth: "100%" }}>
          <Button
            onClick={handleBackToChat}
            style={{ marginBottom: "20px" }}
            icon={<CheckCircleOutlined />}
          >
            Back to AI Chat
          </Button>
          <ImportLocations
            handleCloseModal={() => {
              // Don't do anything - let the import handler manage navigation
              // The history.push in handleImport will handle the redirect
            }}
            isAdmin={isAdmin}
            aiImportData={csvData}
            setTotalStop={setTotalStop}
            setCurrentStop={setCurrentStop}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="ai-import-page">
      <div className="ai-import-container">
        {/* Chat Messages */}
        <div className="ai-chat-messages">
          {chatMessages.length === 0 && (
            <div className="welcome-message">
              <p>Start by uploading a route file or describing your delivery plan.</p>
              <p>
                Supported formats: PDF, DOCX, XLSX, XLS, CSV, TXT, ODS, ODT (Max 5MB).
                See&nbsp; <a href="https://support.goflexio.com">support.goflexio.com</a>
                &nbsp;for additional usage tips and details.
              </p>
            </div>
          )}

          {chatMessages.map((msg, idx) => (
            <div key={idx} className={`chat-message ${msg.role}`}>
              <div className="message-bubble">
                <div className="message-header">
                  {msg.role === "user"
                    ? "You"
                    : msg.role === "assistant"
                    ? "AI Assistant"
                    : "Error"}
                </div>
                <div className="message-text">{msg.content}</div>
              </div>
            </div>
          ))}

          {isLoading && (
            <div className="chat-message assistant">
              <div className="message-bubble loading">
                <div className="message-header">AI Assistant</div>
                <div className="message-text">
                  <div className="loading-dots">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                  <p>Processing your request...</p>
                  <p className="small-text">
                    This may take 60+ seconds. Please be patient!
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* CSV Preview Table */}
        {csvPreview && csvPreview.data && csvPreview.data.length > 0 && (
          <div className={`csv-preview-section ${csvFading ? "fading" : ""}`}>
            <div className="csv-preview-header">
              <div>
                <h3>CSV Data Preview</h3>
                <p className="csv-hint">Double-click any cell to edit</p>
              </div>
              <Button
                type="primary"
                icon={<DownloadOutlined />}
                onClick={handleDownload}
                size="small"
              >
                Download File
              </Button>
            </div>
            <div className="table-controls">
              <div>
                Showing {Math.min(visibleRows, csvPreview.data.length)} of{" "}
                {csvPreview.data.length} rows
                <span className="table-footer-hint">
                  {" "}
                  (Double-click any cell to edit)
                </span>
              </div>
              {visibleRows < csvPreview.data.length && (
                <Button
                  type="link"
                  onClick={() =>
                    setVisibleRows((prev) => Math.min(prev + 50, csvPreview.data.length))
                  }
                  size="small"
                >
                  Load 50 More Rows
                </Button>
              )}
            </div>
            <div className="csv-table-wrapper">
              <table className="csv-table">
                <thead>
                  <tr>
                    {csvPreview.headers.map((header, idx) => (
                      <th key={idx}>{header}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {csvPreview.data.slice(0, visibleRows).map((row, rowIdx) => (
                    <tr key={rowIdx}>
                      {csvPreview.headers.map((header, colIdx) => {
                        const isEditing =
                          editingCell?.rowIndex === rowIdx &&
                          editingCell?.columnName === header;

                        return (
                          <td
                            key={colIdx}
                            className={isEditing ? "editing" : "editable"}
                            onDoubleClick={() =>
                              handleCellDoubleClick(rowIdx, header, row[header])
                            }
                            title="Double-click to edit"
                          >
                            {isEditing ? (
                              <input
                                type="text"
                                value={editValue}
                                onChange={(e) => setEditValue(e.target.value)}
                                onKeyDown={handleEditKeyDown}
                                onBlur={handleCellEditSave}
                                autoFocus
                                className="cell-edit-input"
                              />
                            ) : (
                              row[header]
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Input Area */}
        <div className="ai-input-area">
          <div className="input-controls">
            {!selectedFile ? (
              /* No file selected - show dragger */
              <Dragger
                beforeUpload={handleFileSelect}
                showUploadList={false}
                accept=".pdf,.docx,.xlsx,.xls,.csv,.txt,.ods,.odt"
                disabled={isLoading}
                className="file-dragger"
              >
                <p className="ant-upload-drag-icon">
                  <InboxOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to upload</p>
                <p className="ant-upload-hint">
                  PDF, DOCX, XLSX, XLS, CSV, TXT, ODS, ODT (Max 5MB)
                </p>
              </Dragger>
            ) : (
              <div className="selected-file-display">
                <div className="file-info">
                  <div className="file-icon">📄</div>
                  <div className="file-details">
                    <div className="file-name">{selectedFile.name}</div>
                    <div className="file-size">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </div>
                  </div>
                </div>
                <Button
                  danger
                  size="small"
                  onClick={() => setSelectedFile(null)}
                  disabled={isLoading}
                >
                  Remove
                </Button>
              </div>
            )}
          </div>

          <div className="text-input-wrapper">
            <TextArea
              value={inputText}
              onChange={(e) => setInputText(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={
                selectedFile
                  ? "Add a message (optional) or just click Send..."
                  : "Describe your route plan or paste route data here..."
              }
              autoSize={{ minRows: 3, maxRows: 6 }}
              disabled={isLoading}
            />
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSubmit}
              disabled={isLoading || (!inputText.trim() && !selectedFile)}
              size="large"
              className="send-button"
            >
              {selectedFile && !inputText.trim() ? "Send File" : "Send"}
            </Button>
          </div>
        </div>

        {/* Bottom Actions */}
        <div className="ai-bottom-actions">
          <a
            href="https://www.goflexio.com/terms"
            className="tos-link"
            onClick={(e) => e.preventDefault()}
          >
            Terms of Service
          </a>
          <Button
            type="primary"
            size="large"
            icon={<CheckCircleOutlined />}
            onClick={handleAcceptAndContinue}
            disabled={!csvData || isLoading}
          >
            Accept and Continue
          </Button>
        </div>
      </div>

      {/* Import Modal */}
      {showImportModal && csvData && (
        <Modal
          open={true}
          title="Download Your File"
          onCancel={handleCloseImportModal}
          footer={[
            <Button key="back" onClick={handleCloseImportModal}>
              Back to AI chat
            </Button>,
            <Button
              key="download"
              icon={<DownloadOutlined />}
              onClick={handleDownload}
              type="primary"
            >
              Download File
            </Button>,
            <Button key="import" type="primary" onClick={handleImportFile}>
              Import this data
            </Button>
          ]}
          width={700}
        >
          <div style={{ padding: "20px 0", lineHeight: "1.8" }}>
            <p style={{ fontSize: "16px", fontWeight: "500", marginBottom: "16px" }}>
              <strong>📥 Please download this file first before importing.</strong>
            </p>
            <p>
              Click the <strong>&quot;Download File&quot;</strong> button to save a copy
              to your computer. You should keep this file so you can use or reuse it in
              the future.
            </p>
            <p>
              Although AI is powerful, it is not perfect. If you get errors on the import
              step, you can go back and ask the AI to fix it. Or you may need to{" "}
              <strong>make manual adjustments to the file</strong>
              and then try importing it.
            </p>
            <p>
              Please see{" "}
              <a
                href="https://support.goflexio.com"
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: "#1890ff" }}
              >
                support.goflexio.com
              </a>{" "}
              for details about import file format if you have any questions.
            </p>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AIImportPage;
